/**
 * SessionCharts.jsx — Graphiques PM, environnement, analyse avancée
 * Extrait de SessionDashboard pour respecter la limite 500 lignes
 * 10.5.313 (D239) : la Vue combinée est remontée sous la barre de période
 * (SessionCombinedChart) ; ce composant rend les graphiques qui la suivent.
 * CO2 (D253, lot 2) : section #co2-chart-container après l'environnement,
 * absente du DOM quand les mesures n'ont pas de CO2 (EkynAir Breathe, Live,
 * mobile, imports antérieurs à 10.5.329) : la page reste alors identique.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTheme, TYPE } from '../constants/theme';
import PMChart      from '../charts/PMChart';
import EnvChart     from '../charts/EnvChart';
import Co2Chart     from '../charts/Co2Chart';
import PM10PcsChart from '../charts/PM10PcsChart';
import AucChart     from '../charts/AucChart';
import BoxplotChart from '../charts/BoxplotChart';
import { hasCo2 } from './period/periodAnalysis';

// windowKey : identité de la période affichée. Le changer remonte les
// graphiques, donc remet le zoom à zéro au changement de période.
const SessionCharts = ({ readings, annotations, darkMode, onQuickAnnotate, onAnalyzeZoom, windowKey = 'session' }) => {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const theme = getTheme(darkMode);
  const showCo2 = useMemo(() => hasCo2(null, readings), [readings]);

  return (
    <>
      {/* PM détaillé */}
      <div id="pm-chart-container" role="region" aria-label={t('charts.aria.pm')}
        style={{ background: darkMode ? '#282845' : '#fff', borderRadius: '12px',
          border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
          padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: '600',
          color: darkMode ? '#E1E1E1' : '#2C3E50', marginBottom: '12px' }}>
          {t('charts.section.pm')}
        </h3>
        <PMChart key={windowKey} readings={readings} annotations={annotations} darkMode={darkMode}
          onQuickAnnotate={onQuickAnnotate} onAnalyzeZoom={onAnalyzeZoom} />
      </div>

      {/* Environnement */}
      <div id="env-chart-container" role="region" aria-label={t('charts.aria.env')}
        style={{ background: darkMode ? '#282845' : '#fff', borderRadius: '12px',
          border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
          padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: '600',
          color: darkMode ? '#E1E1E1' : '#2C3E50', marginBottom: '12px' }}>
          {t('charts.section.env')}
        </h3>
        <EnvChart key={windowKey} readings={readings} darkMode={darkMode} onQuickAnnotate={onQuickAnnotate}
          onAnalyzeZoom={onAnalyzeZoom} />
      </div>

      {/* CO2 : seulement si la session en a (capteur externe, D253). */}
      {showCo2 && (
        <div id="co2-chart-container" role="region" aria-label={t('charts.aria.co2')}
          style={{ background: theme.surface, borderRadius: '12px',
            border: `1px solid ${theme.border}`, padding: '20px', marginBottom: '16px' }}>
          <h3 style={{ ...TYPE.h3, color: theme.textPrimary, marginBottom: '12px' }}>
            {t('charts.section.co2')}
          </h3>
          <Co2Chart key={windowKey} readings={readings} darkMode={darkMode} onAnalyzeZoom={onAnalyzeZoom} />
        </div>
      )}

      {/* Accordéon — Analyse avancée */}
      <div style={{ borderRadius: '12px', overflow: 'hidden',
        border: `2px solid ${darkMode ? 'rgba(27,119,241,0.35)' : '#BFDBFE'}`,
        marginBottom: '16px',
        boxShadow: darkMode ? 'none' : '0 1px 6px rgba(27,119,241,0.08)' }}>
        <button type="button" onClick={() => setShowAdvanced(v => !v)} style={{
          width: '100%', padding: '14px 20px',
          background: darkMode
            ? (showAdvanced ? 'rgba(27,119,241,0.30)' : 'rgba(27,119,241,0.18)')
            : (showAdvanced ? '#DBEAFE' : '#EFF6FF'),
          borderBottom: showAdvanced ? `2px solid ${darkMode ? 'rgba(27,119,241,0.4)' : '#93C5FD'}` : 'none',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: '700',
          color: darkMode ? '#60A5FA' : '#1D4ED8',
          letterSpacing: '0.02em',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            {t('charts.section.advanced')}
            <span style={{ fontSize: '11px', fontWeight: '600',
              color: darkMode ? '#BFDBFE' : '#1D4ED8',
              background: darkMode ? 'rgba(27,119,241,0.25)' : '#BFDBFE',
              padding: '3px 10px', borderRadius: '20px',
              border: `1px solid ${darkMode ? 'rgba(27,119,241,0.4)' : '#93C5FD'}` }}>
              {t('charts.advanced.badge')}
            </span>
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            style={{ transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        {/* Conteneurs des graphiques avancés montés en PERMANENCE (jamais démontés).
            POURQUOI : l'export PDF capture le DOM live ; un démontage conditionnel
            ({showAdvanced && …}) faisait disparaître #pm10pcs-chart-container quand
            l'accordéon était replié → captureElement nul → « Graphique indisponible »
            (bug pdf-pm10pcs). On masque donc par maxHeight:0 + overflow:hidden et NON
            par display:none : display:none annule les dimensions → Chart.js peindrait
            un canvas vide non capturable. Ici les graphiques (hauteur intrinsèque fixe)
            restent peints et mesurables même repliés ; seule la hauteur visible est
            écrêtée → aucun changement d'UX écran (replié par défaut, toggle au clic). */}
        <div
          aria-hidden={!showAdvanced}
          style={{
            background: darkMode ? '#1E1E32' : '#fff',
            padding: showAdvanced ? '4px 0 8px' : 0,
            borderTop: showAdvanced ? `1px solid ${darkMode ? 'rgba(27,119,241,0.25)' : '#BFDBFE'}` : 'none',
            maxHeight: showAdvanced ? 'none' : 0,
            overflow: 'hidden',
          }}
        >
            <div id="pm10pcs-chart-container" style={{
              padding: '20px 20px 16px',
              borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : '#F3F4F6'}`,
            }}>
              <PM10PcsChart key={windowKey} readings={readings} darkMode={darkMode} />
            </div>
            <div id="boxplot-chart-container" style={{
              padding: '20px 20px 16px',
              borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : '#F3F4F6'}`,
            }}>
              <BoxplotChart key={windowKey} readings={readings} darkMode={darkMode} />
            </div>
            <div id="auc-chart-container" style={{ padding: '20px 20px 16px' }}>
              <AucChart key={windowKey} readings={readings} darkMode={darkMode} />
            </div>
        </div>
      </div>
    </>
  );
};

export default SessionCharts;
